import { format, intervalToDuration, parseISO, startOfDay, startOfToday } from 'date-fns';

export type Time = {
  hour: number;
  minute: number;
  second: number;
};

export class DateTimeUtils {
  static readonly daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];
  static readonly datesOfMonth = Array.from(Array(28).keys(), (x) => x + 1);

  static isValidDate(date: string | Date) {
    return !isNaN(Date.parse(date instanceof Date ? date.toISOString() : date));
  }

  static timeStringToTime(time: string) {
    const timeParts = time.split(':');
    return {
      hour: +timeParts[0],
      minute: +timeParts[1],
      second: +timeParts[2]
    };
  }

  static timeToTimeString(time: Time) {
    const hour = `${time.hour}`;
    const minute = `${time.minute}`;
    const second = `${time.second}`;

    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}`;
  }

  static secondsToDuration(seconds: number) {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

    return {
      years: duration.years,
      months: duration.months,
      days: duration.days,
      hours: duration.hours,
      minutes: duration.minutes,
      seconds: duration.seconds
    };
  }

  static withOffset(date: Date) {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
  }

  static toISOString(date: Date) {
    return date.toISOString();
  }

  static startOfToday() {
    return startOfToday();
  }

  static startOfDay(date: Date) {
    return startOfDay(date);
  }

  static toISODate(date?: Date | string) {
    if (typeof date === 'object') return date.toISOString().split('T')[0];
    else if (typeof date === 'string') return date.split('T')[0];
    return;
  }

  static toDateFormat(date: Date) {
    return format(date, 'MM/dd/yyyy');
  }

  static toDateTimeFormat(date: Date) {
    return format(date, 'MMM d, yyyy hh:mm a');
  }

  static toUTCTime({ hour, minute, second }: Time): Time {
    const date = new Date(1970, 0, 1, hour, minute, second);

    return {
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      second: date.getUTCSeconds()
    };
  }

  static toDate(dateString: string): Date {
    return parseISO(dateString);
  }

  /**
   * ! Do not remove this method. This is a wokaroud to fix new Date() issue from Javascript
   * ! Javascript generates Date object with UTC 00:00:00 time if ISO date format is passed
   * ! It creates Local timezone's 00:00:00 if other formats are passed
   * ! We want local timezone's 00:00:00. So we are transforming ISO format to slash(/) format and creating Date Object
   * ! Check the below links for more details
   * ! link1: https://stackoverflow.com/a/2587398/6487148
   * ! link2: https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
   *
   * Returns Date object with local timezone's 00:00:00
   * @param dateString It has to be date in the ISO format (YYYY-MM-DD)
   * @returns Date object for that particular date.
   */
  static isoDateToLocalDate(date: string | Date): Date {
    let d: string[] = [];
    if (date instanceof Date) d = date.toISOString().split('T')[0].split('-');
    if (typeof date === 'string') d = new Date(date).toISOString().split('T')[0].split('-');
    return new Date(`${d[1]}/${d[2]}/${d[0]}`);
  }
}
